import {
    ADDITIONAL_EMAILS_RESOURCE_NAME,
    MAX_CHATS_RESOURCE_NAME,
    TELEGRAM_CHATS_RESOURCE_NAME,
    WEBHOOKS_RESOURCE_NAME,
} from 'shared/config/model/urls';
import {AUTO_SELECT_ID} from 'shared/lib/constants';

import {t} from '@lingui/core/macro';

import {IUserOutput, hasTariffFlag} from 'entities/user';
import {rowsToHeaders} from 'features/http/lib/requestHeaders';
import CreateHttpMonitorType from 'features/http/model/HttpMonitor/CreateHttpMonitorType';
import {idsToIri} from 'shared/api/iriHelper';
import {isTranslate} from 'shared/lib/lang';

export type BatchEditFieldKey =
    | 'check_interval'
    | 'method'
    | 'http_request_timeout'
    | 'locations'
    | 'notify'
    | 'frequent_checking'
    | 'parse_errors'
    | 'screenshot_on_error'
    | 'monitor_content_enabled'
    | 'auth_enabled'
    | 'headers';

export interface IBatchEditField {
    key: BatchEditFieldKey;
    label: () => string;
    formKeys: (keyof CreateHttpMonitorType)[];
    isAvailable: (_user: IUserOutput | null) => boolean;
}

export const BATCH_EDIT_FIELDS: IBatchEditField[] = [
    {
        key: 'check_interval',
        label: () => t`Интервал проверки`,
        formKeys: ['check_interval'],
        isAvailable: () => true,
    },
    {
        key: 'method',
        label: () => t`Метод отправки запроса`,
        formKeys: ['method'],
        isAvailable: () => true,
    },
    {
        key: 'http_request_timeout',
        label: () => t`Таймаут запроса`,
        formKeys: ['http_request_timeout'],
        isAvailable: () => true,
    },
    {
        key: 'locations',
        label: () => t`Локации мониторинга`,
        formKeys: ['locations', 'threshold'],
        isAvailable: () => true,
    },
    {
        key: 'notify',
        label: () => t`Уведомления`,
        formKeys: [
            'notify_by_email',
            'notify_email_confirmed_delay',
            'additional_emails',
            'notify_additional_emails_settings',
            'telegram_chats',
            'notify_telegram_chats_settings',
            'max_chats',
            'webhooks',
            'notify_webhooks_settings',
        ],
        isAvailable: () => true,
    },
    {
        key: 'frequent_checking',
        label: () => t`Учащенный мониторинг`,
        formKeys: ['frequent_checking'],
        isAvailable: (user) => hasTariffFlag(user, 'monitor_frequent_checking') && !isTranslate(),
    },
    {
        key: 'parse_errors',
        label: () => t`Проверка подсистем`,
        formKeys: ['parse_errors', 'parse_errors_type', 'parse_errors_pattern'],
        isAvailable: (user) => hasTariffFlag(user, 'monitor_parse_errors') && !isTranslate(),
    },
    {
        key: 'screenshot_on_error',
        label: () => t`Скриншот страницы ошибки`,
        formKeys: ['screenshot_on_error'],
        isAvailable: (user) => hasTariffFlag(user, 'monitor_screenshot_on_error') && !isTranslate(),
    },
    {
        key: 'monitor_content_enabled',
        label: () => t`Мониторинг контента`,
        formKeys: [
            'monitor_content_enabled',
            'monitor_content_data',
            'monitor_content_logic',
            'monitor_content_notify_logic',
        ],
        isAvailable: (user) => hasTariffFlag(user, 'monitor_content_enabled') && !isTranslate(),
    },
    {
        key: 'auth_enabled',
        label: () => t`Авторизация HTTP Basic`,
        formKeys: ['auth_enabled', 'auth'],
        isAvailable: (user) => hasTariffFlag(user, 'monitor_http_basic_auth_enabled') && !isTranslate(),
    },
    {
        key: 'headers',
        label: () => t`Заголовки запроса`,
        formKeys: ['headers'],
        isAvailable: () => !isTranslate(),
    },
];

export const BATCH_EDIT_FIELD_BY_KEY: Record<string, IBatchEditField> = BATCH_EDIT_FIELDS.reduce(
    (acc, field) => {
        acc[field.key] = field;
        return acc;
    },
    {} as Record<string, IBatchEditField>,
);

export const BODY_DEPENDENT_FIELD_KEYS: BatchEditFieldKey[] = [
    'parse_errors',
    'monitor_content_enabled',
    'screenshot_on_error',
];

const methodAllowsBody = (method: unknown): boolean => method === 'GET' || method === 'POST';

const isFieldSendable = (key: string, method: unknown): boolean =>
    !(BODY_DEPENDENT_FIELD_KEYS.some(fieldKey => fieldKey === key) && !methodAllowsBody(method));

export const hasSendableBatchFields = (addedFields: string[], method: unknown): boolean =>
    addedFields.some(key => isFieldSendable(key, method));

const IRI_RESOURCE: Partial<Record<keyof CreateHttpMonitorType, string>> = {
    telegram_chats: TELEGRAM_CHATS_RESOURCE_NAME,
    max_chats: MAX_CHATS_RESOURCE_NAME,
    webhooks: WEBHOOKS_RESOURCE_NAME,
    additional_emails: ADDITIONAL_EMAILS_RESOURCE_NAME,
};

export function buildBatchEditPayload(
    addedFields: string[],
    formData: CreateHttpMonitorType,
    selectedIds: string[],
): Record<string, unknown> {
    const data: Record<string, unknown> = {ids: selectedIds};

    const activeFormKeys = new Set<keyof CreateHttpMonitorType>();
    addedFields.forEach(key => {
        if (!isFieldSendable(key, formData.method)) {
            return;
        }
        const field = BATCH_EDIT_FIELD_BY_KEY[key];
        if (field) {
            field.formKeys.forEach(fk => activeFormKeys.add(fk));
        }
    });

    activeFormKeys.forEach(fk => {
        if (fk === 'locations') {
            const clean = (((formData.locations as unknown) as string[]) || [])
                .filter(loc => loc !== AUTO_SELECT_ID);
            data.locations = idsToIri(clean, 'locations');
            return;
        }
        if (fk === 'headers') {
            data.headers = formData.headers_mode === 'clear' ? {} : rowsToHeaders(formData.headers);
            return;
        }
        const resource = IRI_RESOURCE[fk];
        if (resource) {
            data[fk] = idsToIri((formData[fk] as unknown) as string[], resource);
            return;
        }
        data[fk] = formData[fk];
    });

    return data;
}
